import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import type { Root } from "react-dom/client";
import { NotifyContainers } from "react-simple-notify";

import { routeTree } from "./routeTree.gen";

import "./main.scss";
import { ROUTES } from "./shared/constants/routes";
import {
  clearExpiredChunkLoadRecoveryState,
  isChunkLoadError,
  recoverFromChunkLoadError,
  type VitePreloadErrorEvent,
} from "./shared/lib/chunk-load-recovery";
import { applyStoredDashboardThemeColor } from "./shared/lib/dashboard-theme-color";
import { isAxiosBffError } from "./shared/lib/is-axios-bff-error";
import { isCabinetAccessDeniedError } from "./shared/lib/is-cabinet-access-denied-error";
import { showErrorPopup } from "./shared/lib/show-error-popup";
import { loadTheme } from "./shared/style-loader";

void loadTheme();
applyStoredDashboardThemeColor();
clearExpiredChunkLoadRecoveryState();

const handleChunkLoadRecovery = (error: unknown) =>
  isChunkLoadError(error) && recoverFromChunkLoadError();

// Reload once when a lazy chunk disappears after a fresh deploy.
window.addEventListener("vite:preloadError", (event) => {
  const preloadErrorEvent = event as VitePreloadErrorEvent;

  if (handleChunkLoadRecovery(preloadErrorEvent.payload)) {
    preloadErrorEvent.preventDefault();
  }
});

window.addEventListener("error", (event) => {
  if (handleChunkLoadRecovery(event.error ?? event.message)) {
    event.preventDefault();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (handleChunkLoadRecovery(event.reason)) {
    event.preventDefault();
  }
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
    mutations: {
      onError: (error) => {
        // The API interceptor already shows the specific cabinet denial.
        if (isCabinetAccessDeniedError(error)) return;
        if (router.latestLocation.pathname !== ROUTES.login) {
          const text = isAxiosBffError(error)
            ? error.response?.data.title
            : undefined;

          const title = isAxiosBffError(error)
            ? error.response?.data.caption
            : undefined;

          showErrorPopup({
            title: title || "Что-то пошло не так",
            text: text || "Пожалуйста, попробуйте еще раз",
          });
        }
      },
    },
  },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app")!;
type RootElement = HTMLElement & { __rendaRoot__?: Root };
const appRootElement = rootElement as RootElement;
const root =
  appRootElement.__rendaRoot__ ??
  (appRootElement.__rendaRoot__ = ReactDOM.createRoot(appRootElement));

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
    <NotifyContainers />
  </StrictMode>,
);
